import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useGlobal } from "../context/GlobalContext";
import { formatImageUrl } from "../utils/formatImageUrl";

// Helper to extract plain number from price strings like "₹499" or "$12.99"
const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (!price) return 0;
    const num = parseFloat(String(price).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? 0 : num;
};

const sortOptions = [
    { value: 'default', label: 'Default', icon: 'ph ph-arrows-down-up' },
    { value: 'price-asc', label: 'Price: Low to High', icon: 'ph ph-trend-up' },
    { value: 'price-desc', label: 'Price: High to Low', icon: 'ph ph-trend-down' },
    { value: 'name-asc', label: 'Name: A to Z', icon: 'ph ph-sort-ascending' },
    { value: 'name-desc', label: 'Name: Z to A', icon: 'ph ph-sort-descending' },
];

const WishListSection = () => {
    const { wishlistItems, removeFromWishlist, addToCart, cartItems } = useGlobal();
    const [grid, setGrid] = useState(false);
    const [sortBy, setSortBy] = useState("default");
    const [isSortOpen, setIsSortOpen] = useState(false);
    const sortRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortRef.current && !sortRef.current.contains(event.target)) {
                setIsSortOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Sorting wishlist products
    const sortedWishlist = [...wishlistItems].sort((a, b) => {
        const nameA = a.productName || a.name || "";
        const nameB = b.productName || b.name || "";
        switch (sortBy) {
            case 'price-asc':
                return parsePrice(a.currentPrice || a.price) - parsePrice(b.currentPrice || b.price);
            case 'price-desc':
                return parsePrice(b.currentPrice || b.price) - parsePrice(a.currentPrice || a.price);
            case 'name-asc':
                return nameA.localeCompare(nameB);
            case 'name-desc':
                return nameB.localeCompare(nameA);
            default:
                return 0;
        }
    });

    return (
        <section className="shop wishlist-section py-80">
            <style>{`
                .pl-wishlist-btn {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .pl-wishlist-btn:hover {
                    transform: scale(1.1);
                }
                @media (max-width: 575px) {
                    .pl-wishlist-btn {
                        top: 8px;
                        left: 8px;
                        width: 28px;
                        height: 28px;
                    }
                }
                /* ---- Modern Sort by Dropdown ---- */
                .sort-by-wrapper {
                    position: relative;
                    user-select: none;
                }
                .sort-by-trigger {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 8px 14px;
                    cursor: pointer;
                    outline: none;
                    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
                }
                .sort-by-trigger:hover,
                .sort-by-trigger.active {
                    border-color: #24593E;
                    box-shadow: 0 0 0 3px rgba(36, 89, 62, 0.12);
                    background: #ffffff;
                }
                .sort-by-trigger .sort-caret {
                    font-size: 11px;
                    color: #64748b;
                    transition: transform 0.2s ease;
                }
                .sort-by-trigger .sort-caret.rotate-180 {
                    transform: rotate(180deg);
                }
                .sort-by-dropdown-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    min-width: 215px;
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 12px 28px -4px rgba(15, 23, 42, 0.12), 0 4px 10px -2px rgba(15, 23, 42, 0.06);
                    padding: 6px;
                    z-index: 1050;
                    animation: sortMenuFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes sortMenuFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-6px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .sort-by-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 8px 12px;
                    border-radius: 8px;
                    font-size: 13.5px;
                    font-weight: 500;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .sort-by-item:hover {
                    background-color: #f8fafc;
                    color: #0f172a;
                }
                .sort-by-item.selected {
                    background-color: #f0fdf4;
                    color: #166534;
                    font-weight: 600;
                }
                .sort-by-item.selected i.ph-check {
                    color: #16a34a;
                }
                @media (max-width: 575px) {
                    .sort-by-dropdown-menu {
                        left: auto;
                        right: 0;
                        min-width: 200px;
                    }
                }
            `}</style>

            <div className="container container-lg">
                {wishlistItems.length === 0 ? (
                    <div className="text-center py-80 border border-gray-100 rounded-16 bg-white p-40">
                        <div 
                            className="rounded-circle flex-center mx-auto mb-20"
                            style={{ width: '80px', height: '80px', background: '#FEF2F2', color: '#EF4444' }}
                        >
                            <i className="ph ph-heart text-4xl" />
                        </div>
                        <h4 className="text-heading mb-8 fw-semibold">Your Wishlist is Empty</h4>
                        <p className="text-gray-500 mb-24 max-w-400 mx-auto">
                            Explore our products and save your favorite items here to purchase later!
                        </p>
                        <Link 
                            to="/shop" 
                            className="btn bg-main-600 hover-bg-main-700 text-white py-12 px-32 rounded-8 inline-flex align-items-center gap-8 fw-medium"
                        >
                            <i className="ph ph-shopping-bag text-lg" /> Explore Shop
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Top Controls Bar */}
                        <div className="flex-between gap-16 flex-wrap mb-40">
                            <span className="text-gray-900 fw-medium">
                                Showing {sortedWishlist.length} {sortedWishlist.length === 1 ? 'item' : 'items'} in wishlist
                            </span>
                            <div className="position-relative flex-align gap-16 flex-wrap">
                                <div className="sort-by-wrapper" ref={sortRef}>
                                    <button
                                        type="button"
                                        className={`sort-by-trigger ${isSortOpen ? 'active' : ''}`}
                                        onClick={() => setIsSortOpen(!isSortOpen)}
                                        aria-expanded={isSortOpen}
                                        aria-haspopup="listbox"
                                    >
                                        <i className="ph ph-sort-ascending text-lg text-main-600" />
                                        <span className="text-sm fw-semibold text-neutral-800">
                                            {(sortOptions.find(o => o.value === sortBy) || sortOptions[0]).label}
                                        </span>
                                        <i className={`ph ph-caret-down sort-caret ${isSortOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {isSortOpen && (
                                        <div className="sort-by-dropdown-menu" role="listbox">
                                            {sortOptions.map((option) => {
                                                const isSelected = sortBy === option.value;
                                                return (
                                                    <div
                                                        key={option.value}
                                                        role="option"
                                                        aria-selected={isSelected}
                                                        className={`sort-by-item ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setSortBy(option.value);
                                                            setIsSortOpen(false);
                                                        }}
                                                    >
                                                        <div className="flex-align gap-8">
                                                            <i className={`${option.icon} text-md ${isSelected ? 'text-main-600' : 'text-gray-500'}`} />
                                                            <span>{option.label}</span>
                                                        </div>
                                                        {isSelected && (
                                                            <i className="ph-bold ph-check text-sm" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="list-grid-btns flex-align gap-16">
                                    <button onClick={() => setGrid(false)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${!grid && "border-main-600 text-white bg-main-600"}`}
                                        title="Grid View"
                                    >
                                        <i className="ph ph-squares-four" />
                                    </button>
                                    <button onClick={() => setGrid(true)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid && "border-main-600 text-white bg-main-600"}`}
                                        title="List View"
                                    >
                                        <i className="ph-bold ph-list-dashes" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Product Cards: Grid vs List */}
                        {!grid ? (
                            /* GRID VIEW — standard responsive 2-column on mobile, 4-5 on desktop */
                            <div className="row g-12 g-sm-16 row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
                                {sortedWishlist.map((product, index) => {
                                    const isInCart = cartItems?.some(item => 
                                        String(item.id) === String(product.id) && 
                                        (product.variantId ? String(item.selectedVariantId) === String(product.variantId) : true)
                                    );

                                    return (
                                        <div key={product.wishlistItemId || `${product.id}_${product.variantId || index}`} className="col">
                                            <div
                                                className="product-card hover-lift p-12 border border-gray-100 hover-border-main-600 rounded-16 position-relative d-flex flex-column h-100 bg-white"
                                            >
                                                {/* Wishlist Active Button (Click to Remove) */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        removeFromWishlist(product.id, product.variantId);
                                                    }}
                                                    className="pl-wishlist-btn border bg-danger-50 border-danger-200 text-danger-600 shadow-xs"
                                                    title="In Wishlist (Click to remove)"
                                                    aria-label="Remove from Wishlist"
                                                >
                                                    <i className="ph-fill ph-heart text-danger-600 text-sm" />
                                                </button>

                                                {/* 1. Photo */}
                                                <Link
                                                    to={`/product-details/${product.slug || product.id}`}
                                                    className="product-card__thumb flex-center"
                                                    style={{ height: '130px', width: '100%', padding: '4px' }}
                                                >
                                                    <img 
                                                        src={formatImageUrl(product.image) || product.image} 
                                                        alt={product.productName || product.name} 
                                                        style={{ width: 'auto', height: '100%', maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                                                    />
                                                </Link>

                                                {/* 2. Middle: Add to Cart / View Cart Button */}
                                                <div className="product-card__action my-8 w-100">
                                                    {isInCart ? (
                                                        <Link
                                                            to="/cart"
                                                            className="product-card__cart btn bg-main-600 text-white hover-bg-main-700 py-8 px-14 text-xs rounded-pill flex-center gap-6 w-100"
                                                        >
                                                            View Cart <i className="ph ph-shopping-cart" />
                                                        </Link>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                addToCart(
                                                                    product, 
                                                                    1, 
                                                                    product.selectedVariant || product.defaultVariant || (product.rawVariants && product.rawVariants.length > 0 ? product.rawVariants[0] : null)
                                                                );
                                                            }}
                                                            className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-8 px-14 text-xs rounded-pill flex-center gap-6 w-100"
                                                        >
                                                            Add to Cart <i className="ph ph-shopping-cart" />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* 3. Text details (Price & Name) */}
                                                <div className="product-card__content mt-4 flex-shrink-0 d-flex flex-column" style={{ flex: '1 1 auto' }}>
                                                    <div className="product-card__price mb-4 d-flex align-items-center justify-content-start flex-wrap gap-6">
                                                        <div className="d-flex align-items-center gap-6 flex-wrap">
                                                            <span className="text-heading text-sm fw-semibold">
                                                                {product.currentPrice}{' '}
                                                                <span className="text-gray-500 fw-normal text-xs">
                                                                    {product.quantityText}
                                                                </span>
                                                            </span>
                                                            {product.originalPrice && (
                                                                <span className="text-gray-400 text-xs fw-semibold text-decoration-line-through">
                                                                    {product.originalPrice}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {product.variantName ? (
                                                            <span
                                                                className="badge rounded-4 fw-medium"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#059669',
                                                                    background: '#ECFDF5',
                                                                    border: '1px solid #A7F3D0',
                                                                    padding: '1px 5px',
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                {product.variantName}
                                                            </span>
                                                        ) : (product.rawVariants && product.rawVariants.length > 1) && (
                                                            <span
                                                                className="badge rounded-4 fw-medium"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#4b5563',
                                                                    background: '#f3f4f6',
                                                                    border: '1px solid #e5e7eb',
                                                                    padding: '1px 5px',
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0
                                                                }}
                                                                title={`${product.rawVariants.length} options available`}
                                                            >
                                                                +{product.rawVariants.length} options
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h6 className="title text-sm fw-semibold mb-0" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.4', height: '2.8em' }}>
                                                        <Link to={`/product-details/${product.slug || product.id}`} className="link text-line-2">
                                                            {product.productName || product.name}
                                                        </Link>
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* LIST VIEW */
                            <div className="d-flex flex-column gap-16">
                                {sortedWishlist.map((product) => {
                                    const isInCart = cartItems?.some(item => 
                                        String(item.id) === String(product.id) && 
                                        (product.variantId ? String(item.selectedVariantId) === String(product.variantId) : true)
                                    );

                                    return (
                                        <div 
                                            key={product.wishlistItemId || `${product.id}_${product.variantId}`} 
                                            className="p-16 border border-gray-100 hover-border-main-600 rounded-16 bg-white d-flex align-items-center justify-content-between flex-wrap gap-16 transition-2 hover-lift"
                                        >
                                            <div className="d-flex align-items-center gap-16 flex-grow-1 min-w-0">
                                                <Link
                                                    to={`/product-details/${product.slug || product.id}`}
                                                    className="flex-center rounded-12 bg-gray-50 flex-shrink-0"
                                                    style={{ width: '80px', height: '80px', padding: '6px' }}
                                                >
                                                    <img 
                                                        src={formatImageUrl(product.image) || product.image} 
                                                        alt={product.productName || product.name} 
                                                        style={{ objectFit: 'contain', maxHeight: '100%', maxWidth: '100%' }} 
                                                    />
                                                </Link>

                                                <div className="min-w-0 flex-grow-1">
                                                    <h6 className="title text-sm fw-semibold mb-6">
                                                        <Link to={`/product-details/${product.slug || product.id}`} className="link text-line-1">
                                                            {product.productName || product.name}
                                                        </Link>
                                                    </h6>
                                                    <div className="d-flex align-items-center gap-8 flex-wrap">
                                                        <span className="text-heading text-sm fw-semibold">
                                                            {product.currentPrice}{' '}
                                                            <span className="text-gray-500 fw-normal text-xs">
                                                                {product.quantityText}
                                                            </span>
                                                        </span>
                                                        {product.originalPrice && (
                                                            <span className="text-gray-400 text-xs fw-semibold text-decoration-line-through">
                                                                {product.originalPrice}
                                                            </span>
                                                        )}
                                                        {product.variantName ? (
                                                            <span
                                                                className="badge rounded-4 fw-medium"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#059669',
                                                                    background: '#ECFDF5',
                                                                    border: '1px solid #A7F3D0',
                                                                    padding: '1px 5px',
                                                                }}
                                                            >
                                                                {product.variantName}
                                                            </span>
                                                        ) : (product.rawVariants && product.rawVariants.length > 1) && (
                                                            <span
                                                                className="badge rounded-4 fw-medium"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#4b5563',
                                                                    background: '#f3f4f6',
                                                                    border: '1px solid #e5e7eb',
                                                                    padding: '1px 5px',
                                                                }}
                                                                title={`${product.rawVariants.length} options available`}
                                                            >
                                                                +{product.rawVariants.length} options
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-12 flex-shrink-0">
                                                {isInCart ? (
                                                    <Link
                                                        to="/cart"
                                                        className="btn bg-main-600 text-white hover-bg-main-700 py-8 px-20 text-xs rounded-pill flex-align gap-6"
                                                    >
                                                        View Cart <i className="ph ph-shopping-cart" />
                                                    </Link>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            addToCart(
                                                                product, 
                                                                1, 
                                                                product.selectedVariant || product.defaultVariant || (product.rawVariants && product.rawVariants.length > 0 ? product.rawVariants[0] : null)
                                                            );
                                                        }}
                                                        className="btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-8 px-20 text-xs rounded-pill flex-align gap-6"
                                                    >
                                                        Add to Cart <i className="ph ph-shopping-cart" />
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        removeFromWishlist(product.id, product.variantId);
                                                    }}
                                                    className="btn p-8 rounded-circle border border-danger-200 bg-danger-50 text-danger-600 hover-bg-danger-600 hover-text-white"
                                                    title="Remove from Wishlist"
                                                    aria-label="Remove"
                                                >
                                                    <i className="ph ph-trash text-base" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

export default WishListSection;